import React, { useState, useMemo, useEffect } from "react";
import Input from "../../../components/ui/input";
import Button from "../../../components/ui/button";
import Table from "../../../components/ui/table";
import styles from "./budgets.module.css";
import FormSection from "../../../components/screens/form-section";
import { MyIcons } from "../../../components/icons/my-icons";
import Dropdown from "../../../components/ui/dropdown";
import SendBudgetModal from "../../../components/modals/SendBudgetModal";
import { showSuccessToast, showErrorToast, notifications } from "../../../utils/notifications";
import { formatDateToSpanish } from "../../../utils/dateUtils";

import { useOutletContext, useParams } from "react-router-dom";
//SERVICES
import { getBudgetNotesSummary, getBudgetSummary, editBudget, getBudgetById, generateBudgetPDF, sendBudget } from "../../../services/apiService";
import usePageTitle from "../../../hooks/usePageTitle";
import UnsavedChangesModal from "../../../components/modals/unsaved-changes-modal";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

const Budgets = () => {
  const { id: expedienteId } = useParams();
  usePageTitle(`Expediente ${expedienteId}`, 'Presupuestos');
  const [formData, setFormData] = useState({
    presupuestoId: null,
    subject: "",
    budgetNumber: null,
    budgetNotes: [], // Cambiar a array para multiselect
    date: "",
    observations: "",
    iva: null,
  });
  const { recordData, isLockedByOther } = useOutletContext();
  const [budgetNotesOptions, setBudgetNotesOptions] = useState([]);
  const [budgetsTableData, setBudgetsTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendBudgetModalOpen, setSendBudgetModalOpen] = useState(false);
  const [selectedBudgetForSend, setSelectedBudgetForSend] = useState(null);

  // Estados para detectar cambios sin guardar
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hook para advertir cambios sin guardar
  const { showModal, handleCancel, handleConfirm, message } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    "Tienes cambios sin guardar en el presupuesto."
  );

  // Función para refrescar los presupuestos (separada para poder reutilizar)
  const refreshBudgets = async () => {
    console.log("🔄 refreshBudgets ejecutándose...");
    try {
      setLoading(true);
      const budgets = await getBudgetSummary(parseInt(expedienteId.toString().replace(/\s+/g, '')));
      console.log("Presupuestos obtenidos:", budgets);
      setBudgetsTableData(
        budgets.map((budget) => ({
          presupuestoId: budget.presupuestoId, // Agregar el ID del presupuesto para la edición
          numPresupuesto: budget.numPresupuesto,
          asunto: budget.asunto || "Sin asunto",
          fecha: budget.fecha ? formatDateToSpanish(budget.fecha) : "Sin fecha",
          presupuestoTotal:
            budget.presupuestoTotal != null
              ? budget.presupuestoTotal.toFixed(2) + " €"
              : "0.00 €",
          costeTotal:
            budget.costeTotal != null
              ? budget.costeTotal.toFixed(2) + " €"
              : "0.00 €",
          conjuntoTotal:
            budget.conjuntoTotal != null
              ? budget.conjuntoTotal.toFixed(4)
              : "0.00",
          porcentageBeneficioTotal:
            budget.porcentageBeneficioTotal != null
              ? budget.porcentageBeneficioTotal.toFixed(2) + " %"
              : "0.00 %",
          iva: budget.iva || "Sin IVA",
        }))
      );
    } catch (error) {
      console.error("Error fetching budgets:", error);
      showErrorToast("Error al cargar los presupuestos");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    setFormData({ ...formData, [field]: option });
  };

  // useEffect para detectar cambios sin guardar
  useEffect(() => {
    if (!initialFormData || !formData.presupuestoId) {
      setHasUnsavedChanges(false);
      return;
    }

    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setHasUnsavedChanges(formChanged);
  }, [formData, initialFormData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.subject?.trim()) {
      showErrorToast("Por favor ingresa un asunto para el presupuesto");
      return;
    }

    if (!formData.presupuestoId) {
      showErrorToast("Solo se pueden editar presupuestos existentes");
      return;
    }
    
    try {
      // Formatear la fecha al formato yyyy-mm-dd
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // yyyy-mm-dd
      };

      // Construir el cuerpo de la petición según la API
      const budgetData = {
        fecha: formatDate(formData.date),
        expedienteId: parseInt(expedienteId.toString().replace(/\s+/g, '')),
        asunto: formData.subject,
        observaciones: formData.observations,
        numPresupuesto: formData.budgetNumber,
        iva: formData.iva?.value || formData.iva,
        notasPresupuestoId: formData.budgetNotes.map(note => note.value),
      };
      console.log("📤 Enviando datos del presupuesto:", budgetData);
      
      // Editar presupuesto existente
      await editBudget(formData.presupuestoId, budgetData);
      showSuccessToast(notifications.update.success("Presupuesto"));
      
      // Actualizar la tabla de presupuestos
      refreshBudgets();
      
      // Limpiar el formulario y resetear estado de cambios
      setInitialFormData(null);
      setHasUnsavedChanges(false);
      setFormData({
        presupuestoId: null,
        subject: "",
        budgetNumber: null,
        budgetNotes: [], // Array vacío para multiselect
        date: "",
        observations: "",
        iva: null,
      });
    } catch (error) {
      console.error("Error al guardar el presupuesto:", error);
      showErrorToast(error.response?.data?.message || notifications.save.error);
    }
  };

  const handleBudgetClick = async (budget) => {
    console.log("🔍 Obteniendo detalles completos del presupuesto:", budget.presupuestoId);
    try {
      // Obtener los detalles completos del presupuesto desde la API
      const budgetDetails = await getBudgetById(budget.presupuestoId);
      console.log("📋 Detalles del presupuesto obtenidos:", budgetDetails);
      
      // Mapear las notas de presupuesto desde los IDs a objetos con value y label
      const selectedNotes = budgetDetails.notasPresupuestoId 
        ? budgetDetails.notasPresupuestoId.map(noteId => {
            const note = budgetNotesOptions.find(option => option.value === noteId);
            return note || { value: noteId, label: `Nota ${noteId}` };
          })
        : [];
      
      const newFormData = {
        presupuestoId: budgetDetails.id,
        subject: budgetDetails.asunto || "",
        budgetNumber: budgetDetails.numPresupuesto || "",
        budgetNotes: selectedNotes, // Array de objetos para multiselect
        date: budgetDetails.fecha || "",
        observations: budgetDetails.observaciones || "",
        iva: budgetDetails.iva ? { value: budgetDetails.iva, label: budgetDetails.iva } : null,
      };
      
      setFormData(newFormData);
      setInitialFormData(newFormData);
    } catch (error) {
      console.error("Error al obtener detalles del presupuesto:", error);
      showErrorToast("Error al cargar los detalles del presupuesto");
      // Fallback al comportamiento anterior si falla la API
      setFormData({
        presupuestoId: budget.presupuestoId,
        subject: budget.asunto === "Sin asunto" ? "" : (budget.asunto || ""),
        budgetNumber: budget.numPresupuesto || "",
        budgetNotes: [], // Array vacío si no se pueden cargar las notas
        date: budget.fecha === "Sin fecha" ? "" : (budget.fecha || ""),
        observations: "",
        iva: (budget.iva && budget.iva !== "Sin IVA") ? { value: budget.iva, label: budget.iva } : null,
      });
    }
  };

  const handleDownload = async (budget) => {
    try {
      console.log("Descargar PDF:", budget);
      // Generar el PDF y obtener la ruta
      const fileUrl = await generateBudgetPDF(budget.presupuestoId);
      console.log("URL del archivo:", fileUrl);
      
      // Llamar al WordLauncher en silencio (sin abrir pestaña)
      await fetch(fileUrl).catch(() => {});
    } catch (error) {
      console.error("Error al descargar PDF:", error);
      showErrorToast("Error al generar el PDF del presupuesto");
    }
  };

  const handleSend = (budget) => {
    console.log("Enviar presupuesto:", budget);
    setSelectedBudgetForSend(budget);
    setSendBudgetModalOpen(true);
  };

  const handleSendBudget = async (sendData) => {
    try {
      console.log("📤 Enviando presupuesto con datos:", sendData);
      await sendBudget(selectedBudgetForSend.presupuestoId, sendData);
      showSuccessToast("Presupuesto enviado correctamente");
      setSendBudgetModalOpen(false);
      setSelectedBudgetForSend(null);
    } catch (error) {
      console.error("Error al enviar presupuesto:", error);
      showErrorToast(error.response?.data?.message || "Error al enviar el presupuesto");
    }
  };

  const handleCancelSendBudget = () => {
    setSendBudgetModalOpen(false);
    setSelectedBudgetForSend(null);
  };

  // Opciones para los dropdowns - budgetNumberOptions eliminado ya que ahora es un Input

  const ivaOptions = [
    { value: "10%", label: "10%" },
    { value: "21%", label: "21%" },
  ];

  /*const budgetNotesOptions = [
    { value: "note1", label: "Presupuesto Estándar" },
    { value: "note2", label: "Presupuesto Urgente" },
    { value: "note3", label: "Presupuesto con Descuento" },
  ];*/

  // Encabezados para la tabla de presupuestos
  const budgetsTableHeaders = [
    { label: "Nº de presupuesto", key: "numPresupuesto" },
    { label: "Asunto", key: "asunto" },
    { label: "Fecha", key: "fecha" },
    { label: "Presupuesto", key: "presupuestoTotal" },
    { label: "Coste", key: "costeTotal" },
    { label: "R. Conjunto", key: "conjuntoTotal" },
    { label: "% Beneficio", key: "porcentageBeneficioTotal" },
    { label: "IVA", key: "iva" },
  ];

  const isFormValid = useMemo(() => {
    return (
      formData.subject.trim() !== "" &&
      formData.budgetNumber !== "" &&
      formData.date.trim() !== "" &&
      formData.iva !== null
    );
  }, [
    formData.subject,
    formData.budgetNumber,
    formData.date,
    formData.iva,
  ]);

  useEffect(() => {
    console.log("🔄 useEffect ejecutándose con recordData:", recordData);
    
    const fetchBudgetNotes = async () => {
      try {
        console.log("📝 Cargando notas de presupuesto...");
        const notes = await getBudgetNotesSummary();
        console.log("📝 Notas obtenidas:", notes);
        setBudgetNotesOptions(
          notes.map((note) => ({ value: note.id, label: note.nombre }))
        );
      } catch (error) {
        console.error("Error fetching budget notes:", error);
        showErrorToast("Error al cargar las notas del presupuesto");
      }
    };

    const fetchBudgets = async () => {
      try {
        console.log("💰 Iniciando carga de presupuestos para expediente:", recordData?.expedienteId);
        setLoading(true);
        const budgets = await getBudgetSummary(recordData?.expedienteId);
        console.log("💰 Presupuestos obtenidos en useEffect:", budgets);
        setBudgetsTableData(
          budgets.map((budget) => ({
            presupuestoId: budget.presupuestoId, // Agregar el ID del presupuesto para la edición
            numPresupuesto: budget.numPresupuesto,
            asunto: budget.asunto || "Sin asunto",
            fecha: budget.fecha ? formatDateToSpanish(budget.fecha) : "Sin fecha",
            presupuestoTotal:
              budget.presupuestoTotal != null
                ? budget.presupuestoTotal.toFixed(2) + " €"
                : "0.00 €",
            costeTotal:
              budget.costeTotal != null
                ? budget.costeTotal.toFixed(2) + " €"
                : "0.00 €",
            conjuntoTotal:
              budget.conjuntoTotal != null
                ? budget.conjuntoTotal.toFixed(4)
                : "0.00",
            porcentageBeneficioTotal:
              budget.porcentageBeneficioTotal != null
                ? budget.porcentageBeneficioTotal.toFixed(2) + " %"
                : "0.00 %",
            iva: budget.iva || "Sin IVA",
          }))
        );
      } catch (error) {
        console.error("Error fetching budgets:", error);
        showErrorToast("Error al cargar los presupuestos");
      } finally {
        setLoading(false);
      }
    };

    if (recordData?.expedienteId) {
      console.log("✅ Condición cumplida - expedienteId existe:", recordData.expedienteId);
      fetchBudgets();
      fetchBudgetNotes();
    } else {
      console.log("❌ Condición NO cumplida - expedienteId:", recordData?.expedienteId);
    }
  }, [recordData]);

  return (
    <div style={{ paddingTop: "24px" }}>
      {formData.presupuestoId && (
        <form onSubmit={handleSubmit} style={{ marginBottom: "24px" }}>
          <div>
            <FormSection title="General" style={{ marginBottom: "24px" }}>
              <div className={styles.sectionRow}>
                <Input
                  label="Asunto"
                  name="subject"
                  placeholder={"Asunto"}
                  value={formData.subject}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />

                <Input
                  label="Numero de presupuesto"
                  name="budgetNumber"
                  value={formData.budgetNumber || ""}
                  disabled={true}
                  style={{ flex: 1 }}
                />

              <Dropdown
                label={"Notas de presupuestos"}
                options={budgetNotesOptions}
                onSelect={(option) =>
                  handleDropdownSelect("budgetNotes", option)
                }
                style={{ marginTop: "3px", width: "100%" }}
                mainStyle={{ flex: 1 }}
                selected={formData.budgetNotes}
                multiSelect={true}
              />

              <Dropdown
                label={"IVA"}
                options={ivaOptions}
                onSelect={(option) =>
                  handleDropdownSelect("iva", option)
                }
                style={{ marginTop: "3px", width: "100%" }}
                mainStyle={{ flex: 1 }}
                selected={formData.iva}
              />

              <Input
                label="Fecha"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                style={{ flex: 1 }}
              />
            </div>

            <div className={styles.sectionRow}>
              <Input
                label="Observaciones"
                name="observations"
                placeholder={"Observaciones"}
                value={formData.observations}
                onChange={handleInputChange}
                style={{ width: "100%" }}
              />
            </div>
            <Button type="submit" disabled={!isFormValid || isLockedByOther}>
              Guardar presupuesto
            </Button>
          </FormSection>
        </div>
      </form>
      )}

      <div className={styles.tableContainer}>
        <h3 className={styles.tableTitle}>Presupuestos</h3>
        <Table
          headers={budgetsTableHeaders}
          data={budgetsTableData}
          onPress={isLockedByOther ? null : handleBudgetClick}
          renderActions={(row) => (
            <div className={styles.actionButtons}>
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(row);
                }}
              >
                <MyIcons
                  name="downloadPdf"
                  width={24}
                  height={24}
                  color={"var(--main)"}
                />
              </button>
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSend(row);
                }}
              >
                <MyIcons
                  name="send"
                  width={24}
                  height={24}
                  color={"var(--main)"}
                />
              </button>
            </div>
          )}
        />
      </div>

      {/* Modal para enviar presupuesto */}
      <SendBudgetModal
        open={sendBudgetModalOpen}
        budgetId={selectedBudgetForSend?.presupuestoId}
        expedienteId={recordData?.expedienteId}
        onSend={handleSendBudget}
        onCancel={handleCancelSendBudget}
      />

      {/* Modal de advertencia de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        message={message}
      />
    </div>
  );
};

export default Budgets;
